import React, { useEffect, useState } from 'react'

import { useAdminAuthenticationActions } from 'redux/actions/Admin/Authentication'

import { useTranslation } from 'language/translator'

import { useAccountSettings, useAzureAd } from 'redux/hooks/SignedInAccount'
import { useGroups } from 'redux/hooks/Groups'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import IconButton from 'components_new/atoms/IconButton'
import Input from 'components/common/Input'
import Link from 'components_new/atoms/Link'
import Select from 'components_new/atoms/Select'
import Text from 'components_new/atoms/Text'

import MenuItem from 'components_new/atoms/Menu/MenuItem'
import NewSettingsItem from 'components_new/molecules/NewSettingsItem'
import SwitchItem from 'components_new/molecules/SwitchItem'

import DetailsDialog from './dialogs/DetailsDialog'
import Domain from './Domain'
import SettingsTable from './SettingsTable'
import StatusBox from 'components_new/molecules/StatusBox'

import { NOT_ALLOWED_SX_PROPS } from 'themes'

const INITIAL_VALUES = {
  metadataUrl: '',
  domains: [{ name: '' }],
  idp_groups: []
}

const Authentication = () => {
  const azureAd = useAzureAd()
  const { allowAccess } = useAccountSettings()
  const { tryGetAzureAd, tryPutAzureAd, tryDeleteAzureAd } =
    useAdminAuthenticationActions()

  const [instructionDialogOpen, setInstructionDialogOpen] = useState(false)
  const [activated, setActivated] = useState(false)
  const [values, setValues] = useState(INITIAL_VALUES)

  const { t } = useTranslation()
  const groups = useGroups()

  useEffect(() => {
    if (!azureAd.fetched) {
      tryGetAzureAd()
    }
  }, [])

  useEffect(() => {
    if (azureAd.fetched) {
      const groupedIdpGroups = (azureAd.idpGroups || []).reduce((acc, g) => {
        const existing = acc.find((x) => x.idp_group_id === g.idp_group_id)

        if (existing) {
          if (g.group_id) existing.group_ids.push(g.group_id)
        } else {
          acc.push({
            idp_group_id: g.idp_group_id,
            group_ids: g.group_id ? [g.group_id] : []
          })
        }

        return acc
      }, [])

      setValues({
        metadataUrl: azureAd.metadataUrl,
        domains: [...azureAd.domains, { name: '' }],
        group_id: azureAd.groupId,
        idp_groups: groupedIdpGroups
      })
      setActivated(azureAd.metadataUrl ? true : false)
    }
  }, [azureAd])

  const handleChange = () => {
    setActivated(!activated)
  }

  return (
    <>
      {!allowAccess.sso ? (
        <StatusBox severity="info" title={t('sso', 'sso_not_in_license_title')}>
          {t('sso', 'sso_not_in_license_body')}
        </StatusBox>
      ) : null}

      <Box
        sx={{
          ...(!allowAccess.sso ? NOT_ALLOWED_SX_PROPS : null)
        }}
      >
        <NewSettingsItem
          actions={<SwitchItem onChange={handleChange} checked={activated} />}
          helperText={t('sso', 'entra_id_helper_text')}
          title={t('sso', 'entra_id_title')}
        />

        <NewSettingsItem
          helperText={t('sso', 'specifications_helper_text')}
          title="Specifikationer"
        >
          <SettingsTable />
        </NewSettingsItem>

        <NewSettingsItem
          helperText={t('sso', 'details_helper_text')}
          title={t('sso', 'details_title')}
        >
          <Input
            label={t('sso', 'app_federation_metadata_url_label')}
            disabled={!activated}
            value={values.metadataUrl}
            onChange={(e) => setValues({ ...values, metadataUrl: e })}
          />
          <Text color="text.secondary" variant="caption">
            {t('sso', 'from_microsoft_azure_ad_caption')}
          </Text>
          <Input
            label={t('sso', 'group_id_label')}
            disabled={!activated}
            value={values.group_id}
            onChange={(e) => setValues({ ...values, group_id: e })}
          />
          <Text color="text.secondary" variant="caption">
            {t('sso', 'group_id_helper_text')}
          </Text>
        </NewSettingsItem>

        <NewSettingsItem
          helperText={t('sso', 'mail_domains_helper_text')}
          title={t('sso', 'mail_domains_title')}
        >
          <StatusBox severity="info" title={t('sso', 'warning_note_title')}>
            {t('sso', 'warning_note_body')}
          </StatusBox>

          {values.domains.map((domain, i) => (
            <Domain
              isNew={i === values.domains.length - 1}
              add={() =>
                setValues({
                  ...values,
                  domains: [...values.domains, { name: '' }]
                })
              }
              remove={() =>
                setValues({
                  ...values,
                  domains: values.domains.filter((d) => domain.name !== d.name)
                })
              }
              key={`domain-${i}`}
              value={domain.name}
              onChange={(e) =>
                setValues({
                  ...values,
                  domains: values.domains.map((d, index) =>
                    index === i ? { name: e } : d
                  )
                })
              }
            />
          ))}
        </NewSettingsItem>

        <NewSettingsItem
          helperText={t('sso', 'idp_groups_helper_text')}
          title={t('sso', 'idp_groups_title')}
        >
          {values.idp_groups.map((idpGroup, i) => (
            <Box
              key={`idp-group-${i}`}
              sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}
            >
              <Input
                disabled={!activated}
                label={t('sso', 'idp_group_id_label')}
                onChange={(e) =>
                  setValues({
                    ...values,
                    idp_groups: values.idp_groups.map((g, idx) =>
                      idx === i ? { ...g, idp_group_id: e } : g
                    )
                  })
                }
                value={idpGroup.idp_group_id || ''}
              />
              <Select
                disabled={!activated}
                multiple
                onChange={(e) =>
                  setValues({
                    ...values,
                    idp_groups: values.idp_groups.map((g, idx) =>
                      idx === i ? { ...g, group_ids: e.target.value } : g
                    )
                  })
                }
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((id) => {
                      const group = (groups.allGroups || []).find(
                        (g) => g.id === id
                      )

                      return (
                        <Chip key={id} label={group?.name || id} size="small" />
                      )
                    })}
                  </Box>
                )}
                value={idpGroup.group_ids}
              >
                {(groups.allGroups || []).map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                disabled={!activated}
                onClick={() =>
                  setValues({
                    ...values,
                    idp_groups: values.idp_groups.filter((_, idx) => idx !== i)
                  })
                }
              >
                <span>✕</span>
              </IconButton>
            </Box>
          ))}
          <Button
            disabled={!activated}
            onClick={() =>
              setValues({
                ...values,
                idp_groups: [
                  ...values.idp_groups,
                  { idp_group_id: '', group_ids: [] }
                ]
              })
            }
            variant="outlined"
          >
            {t('sso', 'add_idp_group')}
          </Button>
        </NewSettingsItem>

        <Box>
          <Button
            fullWidth={true}
            onClick={async () => {
              if (activated) {
                try {
                  const domains = values.domains

                  if (domains.length > 1) {
                    domains.pop() // Remove not added domain.
                  }

                  tryPutAzureAd({
                    data: {
                      attributes: {
                        metadata_url: values.metadataUrl,
                        domains: domains,
                        group_id: values.group_id,
                        idp_groups: values.idp_groups
                          .filter((g) => g.group_ids.length > 0)
                          .flatMap((g) =>
                            g.group_ids.map((gid) => ({
                              idp_group_id: g.idp_group_id,
                              group_id: gid
                            }))
                          )
                      }
                    }
                  })
                } catch (e) {}
              } else {
                tryDeleteAzureAd()
              }
            }}
            sx={{ my: 2 }}
          >
            {t('global', 'save')}
          </Button>
        </Box>

        <Text color="text.secondary" variant="caption">
          {t('sso', 'need_help_caption')}{' '}
          <Link onClick={() => setInstructionDialogOpen(true)}>
            {t('sso', 'show_detailed_instructions_link')}
          </Link>
        </Text>
      </Box>

      {/* ----- Dialogs ----- */}
      <DetailsDialog
        open={instructionDialogOpen}
        onClose={() => setInstructionDialogOpen(false)}
      />
    </>
  )
}

export default Authentication
